import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PlanetName, Language, PlanetPosition } from '../types/kundali';
import {
  EnhancedPlanetaryDignity
} from '../utils/planetaryDignityEngine';
import {
  getPlanetFullName,
  getZodiacName,
  getNakshatraName
} from '../utils/language';
import {
  Sparkles,
  Star,
  CheckCircle,
  AlertCircle,
  Shield,
  Award,
  Zap,
  Flame,
  Info,
  Compass,
  HelpCircle,
  BookOpen,
  Activity,
  Layers,
  ArrowUpRight
} from 'lucide-react';

interface PopoverProps {
  enabled: boolean;
  trigger: React.ReactNode;
  content: React.ReactNode;
  className?: string;
  widthClass?: string;
}

export const PopoverWrapper: React.FC<PopoverProps> = ({
  enabled,
  trigger,
  content,
  className = '',
  widthClass = 'w-80 sm:w-96'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; placeAbove: boolean; maxHeight: number; isMobile: boolean } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const calculatePosition = () => {
    if (!triggerRef.current || typeof window === 'undefined') return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 640 || viewportHeight < 560;
    const popoverWidth = Math.min(380, viewportWidth - 24);

    if (isMobile) {
      setCoords({
        top: 0,
        left: viewportWidth / 2,
        placeAbove: false,
        maxHeight: Math.min(viewportHeight * 0.85, 540),
        isMobile: true
      });
      return;
    }

    // Calculate horizontal position (clamped to screen boundaries)
    let left = rect.left + rect.width / 2;
    const halfWidth = popoverWidth / 2;
    if (left - halfWidth < 12) {
      left = 12 + halfWidth;
    } else if (left + halfWidth > viewportWidth - 12) {
      left = viewportWidth - 12 - halfWidth;
    }

    // Calculate vertical position
    const spaceBelow = viewportHeight - rect.bottom - 16;
    const spaceAbove = rect.top - 16;
    const placeAbove = spaceBelow < 280 && spaceAbove > spaceBelow;

    let top: number;
    let maxHeight: number;

    if (placeAbove) {
      maxHeight = Math.max(220, Math.min(540, spaceAbove));
      top = rect.top - 8;
    } else {
      maxHeight = Math.max(220, Math.min(540, spaceBelow));
      top = rect.bottom + 8;
    }

    setCoords({ top, left, placeAbove, maxHeight, isMobile: false });
  };

  const handleMouseEnter = () => {
    if (!enabled || isPinned) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    calculatePosition();
    timeoutRef.current = setTimeout(() => {
      calculatePosition();
      setIsOpen(true);
    }, 60);
  };

  const handleMouseLeave = () => {
    if (isPinned) return; // Retain if pinned
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 120);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!enabled) return;
    e.stopPropagation();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    calculatePosition();
    setIsPinned(true);
    setIsOpen(true);
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsPinned(false);
    setIsOpen(false);
  };

  // Outside click dismissal
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        setIsPinned(false);
        setIsOpen(false);
      }
    };

    const handleScrollOrResize = () => {
      if (isOpen) {
        calculatePosition();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div
      ref={triggerRef}
      className={`relative inline-block cursor-pointer select-none ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {trigger}
      {enabled && isOpen && coords && typeof document !== 'undefined' && createPortal(
        <>
          {(coords.isMobile || isPinned) && (
            <div 
              className="fixed inset-0 bg-black/30 backdrop-blur-2xs z-[99990] animate-in fade-in duration-150"
              onClick={handleClose}
            />
          )}
          <div
            ref={popoverRef}
            style={
              coords.isMobile
                ? {
                    position: 'fixed',
                    bottom: '16px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 'calc(100vw - 24px)',
                    maxWidth: '400px',
                    maxHeight: `${coords.maxHeight}px`,
                    zIndex: 99999
                  }
                : {
                    position: 'fixed',
                    left: `${coords.left}px`,
                    transform: 'translateX(-50%)',
                    top: coords.placeAbove ? undefined : `${coords.top}px`,
                    bottom: coords.placeAbove ? `${window.innerHeight - coords.top}px` : undefined,
                    width: `${Math.min(380, window.innerWidth - 24)}px`,
                    maxHeight: `${coords.maxHeight}px`,
                    zIndex: 99999
                  }
            }
            className="bg-white dark:bg-[#182624] text-astro-text rounded-2xl border-2 border-amber-300/80 shadow-2xl text-left transition-all duration-150 animate-in fade-in zoom-in-95 pointer-events-auto ring-1 ring-black/10 flex flex-col max-h-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => {
              if (timeoutRef.current) clearTimeout(timeoutRef.current);
            }}
            onMouseLeave={handleMouseLeave}
          >
            {/* Scrollable Content Body */}
            <div className="p-3.5 space-y-2.5 overflow-y-auto overscroll-contain flex-1 scrollbar-thin">
              {content}
            </div>
            
            {/* Fixed Dismiss Footer */}
            <div className="px-3.5 py-2 border-t border-astro-border/70 bg-white dark:bg-[#182624] flex items-center justify-between text-[11px] text-astro-secondary-text shrink-0">
              <span className="text-[10px] opacity-75">Click outside to dismiss</span>
              <button
                onClick={handleClose}
                className="px-2.5 py-0.5 rounded-md bg-amber-600 text-white font-bold hover:bg-amber-700 cursor-pointer transition-colors text-[10.5px]"
              >
                Close
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

/* -------------------------------------------------------------
 * Helper: Why & Significance for Planetary Dignities
 * ------------------------------------------------------------- */
function getDignityWhyAndSignificance(
  planet: PlanetName,
  sign: string,
  dignityLabel: string,
  lang: Language
): { why: string; significance: string } {
  const isHi = lang === 'hi';

  if (dignityLabel.includes('Exalted') || dignityLabel.includes('उच्च')) {
    if (planet === 'Sun') {
      return {
        why: isHi
          ? 'सूर्य मेष (मंगल की अग्नि राशि) में 10° पर उच्च होता है, जहाँ आत्मा की ज्योति, नेतृत्व और जीवन-ऊर्जा का शिखर प्रस्फुटन होता है।'
          : 'Sun is exalted in Aries (fire sign of Mars) up to 10°, where solar vitality, regal authority, and sovereign willpower achieve maximum radiance.',
        significance: isHi
          ? 'यह जातक को उच्च आत्मसम्मान, प्रशासनिक क्षमता, दृढ़ संकल्प तथा समाज में स्वाभाविक प्रतिष्ठा व पिता का प्रबल सहयोग प्रदान करता है।'
          : 'Bestows supreme self-confidence, administrative prowess, unshakeable integrity, and strong royal/paternal patronage.'
      };
    }
    if (planet === 'Moon') {
      return {
        why: isHi
          ? 'चंद्रमा वृषभ (शुक्र की स्थिर पृथ्वी राशि) में 3° पर उच्च होता है, जहाँ मन को स्थिरता, भावनात्मक पोषण और शांति मिलती है।'
          : 'Moon is exalted in Taurus (Venusian fixed earth sign) up to 3°, providing emotional stability, sensory contentment, and deep receptivity.',
        significance: isHi
          ? 'मन स्थिर, रचनात्मक और संतुष्ट रहता है; धन-धान्य, सौंदर्य प्रेम और मातृत्व सुख में प्रचुरता रहती है।'
          : 'Brings mental serenity, poetic creativity, steady prosperity, and profound maternal and domestic contentment.'
      };
    }
    if (planet === 'Mars') {
      return {
        why: isHi
          ? 'मंगल मकर (शनि की पृथ्वी राशि) में 28° पर उच्च होता है, जहाँ मंगल का उग्र पराक्रम शनि के अनुशासन व धैर्य से मिलकर अचूक रणनीति बनता है।'
          : 'Mars is exalted in Capricorn (Saturnian cardinal earth) up to 28°, where raw fiery energy is channeled through Saturnian discipline and relentless focus.',
        significance: isHi
          ? 'असाधारण कार्यकुशलता, तकनीकी महारत, युद्ध/प्रबंधन में विजय और जीवन की विषम चुनौतियों पर विजय पाने का सामर्थ्य।'
          : 'Endows unstoppable executive stamina, engineering acumen, strategic victory, and immense courage under pressure.'
      };
    }
    if (planet === 'Mercury') {
      return {
        why: isHi
          ? 'बुध कन्या (अपनी ही पृथ्वी राशि) में 15° पर उच्च होता है, जहाँ तर्क, विश्लेषण और व्यावहारिक बुद्धि अपनी पराकाष्ठा पर पहुँचती है।'
          : 'Mercury is exalted in Virgo (own mutable earth sign) up to 15°, where analytical discernment, data processing, and precision reach peak clarity.',
        significance: isHi
          ? 'तीक्ष्ण तार्किक बुद्धि, वाणिज्यिक कुशलता, लेखांकन, शोध व वाक्पटुता में असाधारण सफलता।'
          : 'Bestows brilliant analytical intellect, commercial mastery, flawless communication, and problem-solving agility.'
      };
    }
    if (planet === 'Jupiter') {
      return {
        why: isHi
          ? 'बृहस्पति कर्क (चंद्रमा की जल राशि) में 5° पर उच्च होता है, जहाँ गुरु का दिव्य ज्ञान मातृत्व करुणा व आध्यात्मिक प्रेम में रूपांतरित होता है।'
          : 'Jupiter is exalted in Cancer (lunar water sign) up to 5°, where divine wisdom merges with maternal empathy and cosmic devotion.',
        significance: isHi
          ? 'अगाध आध्यात्मिक ज्ञान, सात्विक आचरण, उत्तम संतति, उच्च शिक्षा और गुरुजनों व ईश्वर का सतत आशीर्वाद।'
          : 'Fosters profound wisdom, dharmic integrity, noble progeny, academic triumph, and divine protection.'
      };
    }
    if (planet === 'Venus') {
      return {
        why: isHi
          ? 'शुक्र मीन (बृहस्पति की जल राशि) में 27° पर उच्च होता है, जहाँ भौतिक आकर्षण निस्वार्थ दैवीय प्रेम व परमानंद में परिणत होता है।'
          : 'Venus is exalted in Pisces (Jupiterian water sign) up to 27°, where worldly attraction elevates into selfless devotion and unconditional love.',
        significance: isHi
          ? 'उत्कृष्ट कलात्मक प्रतिभा, वैवाहिक सुख, विलासिता, परोपकारी स्वभाव और जीवन में सहज समृद्धि।'
          : 'Grants refined aesthetic genius, marital harmony, sublime comforts, and benevolent generosity.'
      };
    }
    if (planet === 'Saturn') {
      return {
        why: isHi
          ? 'शनि तुला (शुक्र की वायु राशि) में 20° पर उच्च होता है, जहाँ शनि का कठोर न्याय तुला के संतुलन व निष्पक्षता के साथ पूर्ण न्याय बन जाता है।'
          : 'Saturn is exalted in Libra (Venusian air sign) up to 20°, where karmic justice aligns with diplomatic balance, equity, and fair social order.',
        significance: isHi
          ? 'न्यायप्रियता, जनसमर्थन, दीर्घायु, संगठनात्मक शक्ति और निष्काम कर्म द्वारा दीर्घकालिक स्थायी सफलता।'
          : 'Grants exceptional fairness, organizational longevity, democratic mass support, and enduring worldly achievements.'
      };
    }
    if (planet === 'Rahu' || planet === 'Ketu') {
      return {
        why: isHi
          ? `${planet === 'Rahu' ? 'राहु वृषभ/मिथुन' : 'केतु वृश्चिक/धनु'} में उच्च माना जाता है, जहाँ इसकी सूक्ष्म ऊर्जा जातक के भौतिक/आध्यात्मिक उत्थान को तीव्र गति देती है।`
          : `${planet} is considered exalted here, channelizing intense astral power into high worldly or mystical accomplishments.`,
        significance: isHi
          ? 'अप्रत्याशित लाभ, कूटनीतिक सफलता या गहन आध्यात्मिक अंतर्दृष्टि।'
          : 'Grants sudden breakthroughs, deep esoteric insight, and foreign or technical prominence.'
      };
    }
  }

  if (dignityLabel.includes('Debilitated') || dignityLabel.includes('नीच')) {
    if (planet === 'Mars') {
      return {
        why: isHi
          ? 'मंगल कर्क (चंद्रमा की जल राशि) में नीच होता है, क्योंकि जल तत्व मंगल की उग्र अग्नि को बुझाकर ऊर्जा को आवेगी व अशांत बना देता है।'
          : 'Mars is debilitated in Cancer (lunar water), where passive emotional tides quench its fiery initiative, causing internalized frustration or impulsiveness.',
        significance: isHi
          ? 'धैर्य की कमी, भावनात्मक आक्रोश या रक्त/ऊर्जा संबंधी असंतुलन। नीच भंग होने पर यह ऊर्जा असाधारण रचनात्मक रक्षा-कवच में बदल जाती है।'
          : 'Requires conscious patience and channelization of passions. When Neecha Bhanga occurs, it turns into fierce protective tenacity.'
      };
    }
    if (planet === 'Sun') {
      return {
        why: isHi
          ? 'सूर्य तुला (शुक्र की राशि) में नीच होता है, क्योंकि दूसरों को प्रसन्न करने की प्रवृत्ति में राजा (सूर्य) का स्वतंत्र आत्मबल क्षीण होता है।'
          : 'Sun is debilitated in Libra (Venusian air), where the sovereign King compromises personal authority for external social validation.',
        significance: isHi
          ? 'आत्मविश्वास में संशय, पिता से वैचारिक मतभेद या नेत्र/हड्डियों की संवेदनशीलता; आत्ममंथन व अनुशासन से यह परिपक्व बनता है।'
          : 'Prompts lessons in authentic self-worth versus people-pleasing; strengthened via righteous conviction and solar discipline.'
      };
    }
    if (planet === 'Moon') {
      return {
        why: isHi
          ? 'चंद्रमा वृश्चिक (मंगल की गुप्त जल राशि) में नीच होता है, जहाँ मन अत्यधिक संवेदनशील, संशयी व भावनात्मक गहराई में उलझ जाता है।'
          : 'Moon is debilitated in Scorpio (Martian fixed water), creating psychic intensity, emotional turbulence, and deep vulnerability.',
        significance: isHi
          ? 'मानसिक तनाव या अति-संवेदनशीलता; रूपांतरण के पश्चात यह जातक को अद्वितीय मनोवैज्ञानिक शोध व रहस्य विद्या की शक्ति देता है।'
          : 'Gives intense psychological depth; once mastered, unlocks profound occult insight and healing resilience.'
      };
    }
    if (planet === 'Jupiter') {
      return {
        why: isHi
          ? 'बृहस्पति मकर (शनि की शुष्क पृथ्वी राशि) में नीच होता है, जहाँ व्यावहारिक भौतिकवाद व संदेह गुरु के सहज आशावाद व आस्था को सीमित करते हैं।'
          : 'Jupiter is debilitated in Capricorn (Saturnian rigid earth), where cynical practicality constrains boundless philosophical faith.',
        significance: isHi
          ? 'धर्म व ज्ञान का अत्यधिक भौतिकवादी दृष्टिकोण; व्यावहारिक धरातल पर सचेत नीति अपनाने पर यह स्थायी अर्थलाभ कराता है।'
          : 'Teaches grounding lofty ideals into tangible structures; excellent for pragmatic business when Neecha Bhanga activates.'
      };
    }
    if (planet === 'Venus') {
      return {
        why: isHi
          ? 'शुक्र कन्या (बुध की राशि) में नीच होता है, जहाँ अत्यधिक नुक़्ताचीनी व विश्लेषण प्रेम की सहज मिठास व भावनाओं को शुष्क बना देते हैं।'
          : 'Venus is debilitated in Virgo (Mercurial critical earth), where hyper-analysis and fault-finding chill spontaneous romantic joy.',
        significance: isHi
          ? 'संबंधों में अत्यधिक अपेक्षाएं; व्यावहारिक कला, सेवा भाव व डिज़ाइन में यह असाधारण निपुणता देता है।'
          : 'Channelized best into precision craftsmanship, healthcare, architecture, and unconditional selfless service.'
      };
    }
    if (planet === 'Saturn') {
      return {
        why: isHi
          ? 'शनि मेष (मंगल की उग्र अग्नि राशि) में नीच होता है, जहाँ शनि का स्वाभाविक धैर्य व धीमापन मेष की जल्दबाजी से आहत होता है।'
          : 'Saturn is debilitated in Aries (Martian fire), where hasty impatience clashes with Saturnian methodical discipline.',
        significance: isHi
          ? 'जल्दबाजी में निर्णय लेने से संघर्ष; निरंतर अभ्यास व संयम से यह व्यक्ति को अदम्य कर्मठ बनाता है।'
          : 'Calls for cultivating steady endurance over rash impulses; yields immense tenacity once disciplined.'
      };
    }
    if (planet === 'Mercury') {
      return {
        why: isHi
          ? 'बुध मीन (बृहस्पति की जल राशि) में नीच होता है, जहाँ बुद्धि कल्पनाओं व भावुकता के अथाह सागर में खोकर ठोस तर्क नहीं कर पाती।'
          : 'Mercury is debilitated in Pisces (Jupiterian boundless water), where strict logic dissolves into oceanic imagination.',
        significance: isHi
          ? 'व्यावहारिक दस्तावेजीकरण में सावधानी रखें; यह अद्भुत काव्यात्मक, दार्शनिक व सहज ज्ञान युक्त अंतःप्रेरणा देता है।'
          : 'Outstanding for abstract art, visionary fiction, and spiritual intuition, though requires grounding in worldly accounts.'
      };
    }
  }

  if (dignityLabel.includes('Moolatrikona') || dignityLabel.includes('मूलत्रिकोण')) {
    return {
      why: isHi
        ? `${planet} अपने मूलत्रिकोण क्षेत्र में ग्रह का 'कार्यालय (Office)' माना जाता है, जहाँ यह अपने शुद्धतम नैसर्गिक दायित्वों का निर्वहन करता है।`
        : `${planet} in Moolatrikona operates from its cosmic executive office, expressing its purest constitutional strength.`,
      significance: isHi
        ? 'यह ग्रह अपने सभी शुभ व नैसर्गिक फलों को अत्यंत सहजता व पूर्ण सामर्थ्य के साथ जीवन में साकार करता है।'
        : 'Confers direct, reliable execution of the planet\'s core significations with natural authority and vitality.'
    };
  }

  if (dignityLabel.includes('Own') || dignityLabel.includes('स्वराशि')) {
    return {
      why: isHi
        ? `${planet} अपनी ही स्वराशि में स्थित होने से 'गृहस्वामी' की भांति पूर्ण सुरक्षित, सहज व स्वतंत्र महसूस करता है।`
        : `${planet} residing in its own sign acts as a comfortable homeowner with full autonomy and protective strength.`,
      significance: isHi
        ? 'अपने स्वामित्व वाले भावों की रक्षा करता है और जीवन में स्थिरता, सुरक्षा व आत्मबल सुनिश्चित करता है।'
        : 'Guarantees protection and flourishing of its ruled houses, bestowing self-reliance and enduring stability.'
    };
  }

  if (dignityLabel.includes('Friendly') || dignityLabel.includes('मित्र')) {
    return {
      why: isHi
        ? `${planet} का इस राशि स्वामी के साथ पंचधा मैत्री व तात्विक सामंजस्य है, जिससे यह अनुकूल वातावरण प्राप्त करता है।`
        : `${planet} shares compound Panchadha friendship and elemental resonance with the sign dispositor.`,
      significance: isHi
        ? 'सहयोगात्मक परिस्थितियाँ, मानसिक संतोष और न्यूनतम घर्षण के साथ कार्यों की सिद्धि।'
        : 'Generates supportive circumstances, positive social synergy, and smooth manifestation of endeavors.'
    };
  }

  if (dignityLabel.includes('Enemy') || dignityLabel.includes('शत्रु')) {
    return {
      why: isHi
        ? `${planet} का इस राशि स्वामी के साथ नैसर्गिक या तात्कालिक वैचारिक विरोध है, जिससे ऊर्जा में अवरोध उत्पन्न होता है।`
        : `${planet} encounters ideological and elemental friction with the sign dispositor.`,
      significance: isHi
        ? 'कार्यों में विलंब, आंतरिक द्वंद्व या अतिरिक्त श्रम की आवश्यकता; आत्म-संयम से यह जातक को सतर्क व परिपक्व बनाता है।'
        : 'Prompts growth through conscious adjustments, patience, and overcoming internal resistance.'
    };
  }

  return {
    why: isHi
      ? `${planet} इस राशि में सम (तटस्थ) स्थिति में है, जहाँ इसका प्रभाव अन्य ग्रहों की दृष्टि व युति पर निर्भर करता है।`
      : `${planet} is in a neutral territory, operating smoothly subject to aspects and house positioning.`,
    significance: isHi
      ? 'संतुलित फल, न अधिक अवरोध न अनायास लाभ; कर्म के अनुसार परिणाम प्राप्त होते हैं।'
      : 'Delivers balanced, equitable outcomes proportional to the native\'s conscious efforts and chart alignments.'
  };
}

export function getDignityThemeStyles(
  dignity: string,
  isExalted?: boolean,
  isDebilitated?: boolean,
  hasNB?: boolean
) {
  if (isExalted || dignity.includes('Exalted') || dignity.includes('उच्च')) {
    return {
      badge: 'bg-emerald-100 text-emerald-900 border-emerald-400 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-600',
      banner: 'bg-emerald-50/80 border-emerald-300 text-emerald-950 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-100',
      title: 'text-emerald-900 dark:text-emerald-300',
      accent: 'text-emerald-700 dark:text-emerald-400'
    };
  }
  if (hasNB || dignity.includes('Neecha Bhanga') || dignity.includes('नीच भंग')) {
    return {
      badge: 'bg-amber-100 text-amber-900 border-amber-400 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-600',
      banner: 'bg-amber-50/80 border-amber-300 text-amber-950 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-100',
      title: 'text-amber-900 dark:text-amber-300',
      accent: 'text-amber-700 dark:text-amber-400'
    };
  }
  if (isDebilitated || dignity.includes('Debilitated') || dignity.includes('नीच')) {
    return {
      badge: 'bg-rose-100 text-rose-900 border-rose-400 dark:bg-rose-950/50 dark:text-rose-200 dark:border-rose-600',
      banner: 'bg-rose-50/80 border-rose-300 text-rose-950 dark:bg-rose-950/30 dark:border-rose-800 dark:text-rose-100',
      title: 'text-rose-900 dark:text-rose-300',
      accent: 'text-rose-700 dark:text-rose-400'
    };
  }
  if (dignity.includes('Moolatrikona') || dignity.includes('मूलत्रिकोण')) {
    return {
      badge: 'bg-teal-100 text-teal-900 border-teal-400 dark:bg-teal-950/50 dark:text-teal-200 dark:border-teal-600',
      banner: 'bg-teal-50/80 border-teal-300 text-teal-950 dark:bg-teal-950/30 dark:border-teal-800 dark:text-teal-100',
      title: 'text-teal-900 dark:text-teal-300',
      accent: 'text-teal-700 dark:text-teal-400'
    };
  }
  if (dignity.includes('Own') || dignity.includes('स्वक्षेत्र') || dignity.includes('स्वराशि')) {
    return {
      badge: 'bg-teal-50 text-teal-900 border-teal-300 dark:bg-teal-950/40 dark:text-teal-200 dark:border-teal-700',
      banner: 'bg-teal-50/60 border-teal-200 text-teal-950 dark:bg-teal-950/20 dark:border-teal-800 dark:text-teal-100',
      title: 'text-teal-900 dark:text-teal-300',
      accent: 'text-teal-700 dark:text-teal-400'
    };
  }
  if (dignity.includes('Great Friend') || dignity.includes('अति मित्र')) {
    return {
      badge: 'bg-blue-100 text-blue-900 border-blue-400 dark:bg-blue-950/50 dark:text-blue-200 dark:border-blue-600',
      banner: 'bg-blue-50/80 border-blue-300 text-blue-950 dark:bg-blue-950/30 dark:border-blue-800 dark:text-blue-100',
      title: 'text-blue-900 dark:text-blue-300',
      accent: 'text-blue-700 dark:text-blue-400'
    };
  }
  if (dignity.includes('Friend') || dignity.includes('मित्र')) {
    return {
      badge: 'bg-sky-50 text-sky-900 border-sky-300 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-700',
      banner: 'bg-sky-50/60 border-sky-200 text-sky-950 dark:bg-sky-950/20 dark:border-sky-800 dark:text-sky-100',
      title: 'text-sky-900 dark:text-sky-300',
      accent: 'text-sky-700 dark:text-sky-400'
    };
  }
  if (dignity.includes('Great Enemy') || dignity.includes('अति शत्रु')) {
    return {
      badge: 'bg-rose-100 text-rose-900 border-rose-400 dark:bg-rose-950/50 dark:text-rose-200 dark:border-rose-600',
      banner: 'bg-rose-50/80 border-rose-300 text-rose-950 dark:bg-rose-950/30 dark:border-rose-800 dark:text-rose-100',
      title: 'text-rose-900 dark:text-rose-300',
      accent: 'text-rose-700 dark:text-rose-400'
    };
  }
  if (dignity.includes('Enemy') || dignity.includes('शत्रु')) {
    return {
      badge: 'bg-orange-50 text-orange-900 border-orange-300 dark:bg-orange-950/40 dark:text-orange-200 dark:border-orange-700',
      banner: 'bg-orange-50/60 border-orange-200 text-orange-950 dark:bg-orange-950/20 dark:border-orange-800 dark:text-orange-100',
      title: 'text-orange-900 dark:text-orange-300',
      accent: 'text-orange-700 dark:text-orange-400'
    };
  }
  return {
    badge: 'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600',
    banner: 'bg-slate-50 border-slate-200 text-slate-900 dark:bg-slate-900/40 dark:border-slate-700 dark:text-slate-100',
    title: 'text-slate-900 dark:text-slate-200',
    accent: 'text-slate-700 dark:text-slate-400'
  };
}

/* -------------------------------------------------------------
 * 1. Planet Name Hover Card (Restored with Dynamic Colors + Modifiers)
 * ------------------------------------------------------------- */
export const PlanetHouseInfluenceHoverCard: React.FC<{
  planet: PlanetPosition;
  houseNumber: number;
  rolesEn: string[];
  rolesHi: string[];
  dignityLabel: string;
  funcNature: string;
  shadbalaRatio?: number;
  shadbalaCategory?: string;
  shadbalaRupas?: number;
  detailedResult: string;
  enhancedDignity?: EnhancedPlanetaryDignity;
  lang: Language;
}> = ({
  planet,
  houseNumber,
  rolesEn,
  rolesHi,
  dignityLabel,
  funcNature,
  shadbalaRatio,
  shadbalaCategory,
  shadbalaRupas,
  detailedResult,
  enhancedDignity,
  lang
}) => {
  const isHi = lang === 'hi';
  const roles = isHi ? rolesHi : rolesEn;
  const pName = planet.name as PlanetName;
  const { why, significance } = getDignityWhyAndSignificance(pName, planet.sign, dignityLabel, lang);
  const theme = getDignityThemeStyles(
    dignityLabel,
    enhancedDignity?.isExalted,
    enhancedDignity?.isDebilitated,
    enhancedDignity?.neechaBhanga?.isActive
  );

  return (
    <div className="space-y-3 text-xs text-astro-text">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-astro-border/70 pb-2.5">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg font-bold flex items-center justify-center text-xs text-white shadow-xs ${
            enhancedDignity?.isExalted
              ? 'bg-emerald-600'
              : enhancedDignity?.isDebilitated
              ? enhancedDignity?.neechaBhanga?.isActive
                ? 'bg-amber-600'
                : 'bg-rose-600'
              : 'bg-astro-primary'
          }`}>
            {planet.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="font-bold text-astro-primary text-sm flex items-center gap-1.5 font-serif">
              <span>{getPlanetFullName(pName, lang)}</span>
              {planet.isRetrograde && (
                <span className="text-[9px] bg-amber-100 text-amber-900 border border-amber-300 font-bold px-1 rounded">
                  {isHi ? 'वक्री (R)' : '(R) Retrograde'}
                </span>
              )}
              <span className="text-[10px] text-astro-secondary-text font-mono font-normal">
                ({getZodiacName(planet.sign, lang)} {(planet.degreeInSign ?? 0).toFixed(2)}°)
              </span>
            </div>
            <div className="text-[9.5px] text-astro-secondary-text">
              {isHi ? `भाव ${houseNumber} में ग्रहीय प्रभाव एवं फलादेश` : `Planetary Influence & Result in House ${houseNumber}`}
            </div>
          </div>
        </div>

        <span className={`text-[9.5px] font-bold px-2 py-0.5 rounded-full border shadow-2xs ${theme.badge}`}>
          {dignityLabel.split(' (')[0]}
        </span>
      </div>

      {/* Role, Karaka and Nature Pills */}
      <div className="flex flex-wrap gap-1.5">
        {roles.map((r, rIdx) => (
          <span
            key={rIdx}
            className={`text-[9.5px] px-2 py-0.5 rounded-md font-semibold border ${
              r.includes('Lord') || r === 'भावेश'
                ? 'bg-amber-50 text-amber-900 border-amber-300'
                : r.includes('Occupant') || r === 'स्थित'
                ? 'bg-indigo-50 text-indigo-900 border-indigo-300'
                : 'bg-stone-100 text-stone-800 border-stone-200'
            }`}
          >
            {r}
          </span>
        ))}
        {enhancedDignity?.karaka && (
          <span className="text-[9.5px] px-2 py-0.5 rounded-md font-semibold bg-purple-50 dark:bg-purple-950/30 text-purple-900 dark:text-purple-200 border border-purple-200 dark:border-purple-800">
            {enhancedDignity.karaka.jaiminiCode ? `★ ${enhancedDignity.karaka.jaiminiCode} • ` : ''}
            {isHi ? enhancedDignity.karaka.sthiraNameHi.split(' ')[0] : enhancedDignity.karaka.sthiraNameEn.split(' ')[0]}
          </span>
        )}
        <span className={`text-[9.5px] px-2 py-0.5 rounded-md font-semibold border ${
          funcNature.includes('Benefic') || funcNature === 'Yogakaraka'
            ? 'bg-emerald-50 text-emerald-900 border-emerald-300'
            : funcNature.includes('Malefic') || funcNature === 'Maraka'
            ? 'bg-rose-50 text-rose-900 border-rose-300'
            : 'bg-astro-card text-astro-secondary-text border-astro-border'
        }`}>
          {funcNature}
        </span>
        <span className="text-[9.5px] px-2 py-0.5 rounded-md bg-astro-card text-astro-text border border-astro-border">
          {planet.nakshatra} ({isHi ? 'पद' : 'P'}{planet.pada})
        </span>
      </div>

      {/* Shadbala Bar if available */}
      {shadbalaRatio !== undefined && (
        <div className="flex items-center justify-between text-[10px] bg-astro-bg/90 p-2 rounded-lg border border-astro-border">
          <span className="font-bold text-astro-primary flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-astro-primary" />
            <span>{isHi ? 'षड्बल सामर्थ्य (Shadbala):' : 'Shadbala Capacity:'}</span>
          </span>
          <div className="flex items-center gap-1.5 font-mono">
            <span className="font-bold text-astro-primary">{shadbalaRatio.toFixed(2)}x</span>
            {shadbalaRupas && <span className="text-astro-secondary-text">({shadbalaRupas} R)</span>}
            <span className={`px-1.5 py-0.2 rounded font-bold text-[8.5px] ${
              shadbalaRatio >= 1.15
                ? 'bg-emerald-100 text-emerald-800'
                : shadbalaRatio >= 1.0
                ? 'bg-teal-100 text-teal-800'
                : 'bg-rose-100 text-rose-800'
            }`}>
              {isHi
                ? (shadbalaRatio >= 1.15 ? 'प्रबल' : shadbalaRatio >= 1.0 ? 'सक्षम' : 'अल्पबल')
                : shadbalaCategory}
            </span>
          </div>
        </div>
      )}

      {/* Retrograde Status Info if active */}
      {planet.isRetrograde && (
        <div className="p-2 rounded-lg bg-amber-50/70 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 text-[10.5px] space-y-0.5 text-amber-950 dark:text-amber-100">
          <div className="font-bold text-amber-900 dark:text-amber-300 flex items-center gap-1">
            <Activity className="w-3.5 h-3.5 text-amber-700" />
            <span>{isHi ? 'वक्री गति प्रभाव (Retrograde Power):' : 'Retrograde Motion Influence:'}</span>
          </div>
          <p className="leading-snug">
            {isHi
              ? 'वक्री होने से ग्रह का चेष्टा बल बढ़ जाता है। यह ग्रह आंतरिक संकल्प, विशिष्ट बौद्धिक चिंतन व विलक्षण दृढ़ता से कार्य करता है।'
              : 'Retrograde status amplifies Cheshta Bala (motional power), manifesting strong internal introspection, unconventional thinking, and persistent karmic force.'}
          </p>
        </div>
      )}

      {/* WHY & ASTROLOGICAL SIGNIFICANCE BOX (Matched theme colors) */}
      <div className={`p-2.5 rounded-lg border space-y-1.5 ${theme.banner}`}>
        <div className={`flex items-center gap-1.5 text-[10.5px] font-bold ${theme.title}`}>
          <HelpCircle className={`w-3.5 h-3.5 shrink-0 ${theme.accent}`} />
          <span>{isHi ? 'कारण (Why) एवं ज्योतिषीय महत्व:' : 'Astrological Reason (Why) & Significance:'}</span>
        </div>
        <div className="space-y-1 text-[11px] leading-relaxed">
          <p>
            <strong className={`font-semibold ${theme.title}`}>{isHi ? 'कारण: ' : 'Why: '}</strong>
            {why}
          </p>
          <p>
            <strong className={`font-semibold ${theme.title}`}>{isHi ? 'महत्व व प्रभाव: ' : 'Significance: '}</strong>
            {significance}
          </p>
        </div>
      </div>

      {/* Detailed Astrological House Result */}
      <div className="bg-astro-bg p-2.5 rounded-lg border border-astro-border space-y-1">
        <div className="text-[10px] font-bold text-astro-primary uppercase tracking-wider flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-astro-primary" />
          <span>{isHi ? `भाव ${houseNumber} में विस्तृत फल (Detailed Result)` : `House ${houseNumber} Manifestation & Result`}</span>
        </div>
        <p className="text-[11px] text-astro-text leading-relaxed">
          {detailedResult}
        </p>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------
 * 2. Dignity & Friendship Hover Content (with Karaka, NB, Retrograde & Dispositor Focus)
 * ------------------------------------------------------------- */
export const DignityHoverCard: React.FC<{
  item: EnhancedPlanetaryDignity;
  lang: Language;
}> = ({ item, lang }) => {
  const isHi = lang === 'hi';
  const { relationships, neechaBhanga, planet, sign, isRetrograde, karaka } = item;
  const { why, significance } = getDignityWhyAndSignificance(
    planet,
    sign,
    isHi ? item.dignityLabelHi : item.dignityLabelEn,
    lang
  );
  const theme = getDignityThemeStyles(
    isHi ? item.dignityLabelHi : item.dignityLabelEn,
    item.isExalted,
    item.isDebilitated,
    neechaBhanga?.isActive
  );

  return (
    <div className="space-y-2.5 text-xs text-astro-text">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-astro-border/70 pb-2">
        <div className="flex items-center gap-1.5 font-bold text-astro-primary text-sm font-serif">
          <Award className="w-4 h-4 text-astro-primary shrink-0" />
          <span>{getPlanetFullName(planet, lang)} {isHi ? 'गरिमा एवं संबंध' : 'Dignity & Relationships'}</span>
        </div>
        <div className="flex items-center gap-1">
          {isRetrograde && (
            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-900 border border-amber-300">
              {isHi ? 'वक्री (R)' : '(R) Vakra'}
            </span>
          )}
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${theme.badge}`}>
            {isHi ? item.dignityLabelHi : item.dignityLabelEn}
          </span>
        </div>
      </div>

      {/* WHY & ASTROLOGICAL SIGNIFICANCE (Matched Theme Colors) */}
      <div className={`p-2.5 rounded-lg border space-y-1 text-[11px] ${theme.banner}`}>
        <div className={`flex items-center gap-1 font-bold text-[10.5px] ${theme.title}`}>
          <BookOpen className={`w-3.5 h-3.5 shrink-0 ${theme.accent}`} />
          <span>{isHi ? 'यह स्थिति क्यों एवं इसका महत्व:' : 'Why This Dignity & Significance:'}</span>
        </div>
        <p>
          <b className={theme.title}>{isHi ? 'कारण: ' : 'Why: '}</b>{why}
        </p>
        <p>
          <b className={theme.title}>{isHi ? 'महत्व: ' : 'Significance: '}</b>{significance}
        </p>
      </div>

      {/* Karaka Signification Box (Sthira & Jaimini Karaka) */}
      {karaka && (
        <div className="p-2.5 rounded-lg bg-purple-50/60 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 text-[11px] space-y-1">
          <div className="flex items-center justify-between font-bold text-purple-900 dark:text-purple-300 text-[10.5px]">
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-purple-700 shrink-0" />
              <span>{isHi ? 'कारक तत्व (Karaka Portfolio):' : 'Karaka Significations:'}</span>
            </div>
            {karaka.jaiminiCode && (
              <span className="text-[9.5px] px-1.5 py-0.2 rounded font-bold bg-purple-200 dark:bg-purple-900 text-purple-900 dark:text-purple-100">
                {isHi ? karaka.jaiminiNameHi : karaka.jaiminiNameEn}
              </span>
            )}
          </div>
          <div className="text-[10.5px] text-purple-950 dark:text-purple-100 space-y-0.5">
            <p>
              <b>{isHi ? 'स्थिर कारक: ' : 'Sthira Karaka: '}</b>
              {isHi ? karaka.sthiraNameHi : karaka.sthiraNameEn} ({isHi ? karaka.sthiraSigHi : karaka.sthiraSigEn})
            </p>
            {karaka.jaiminiSignificanceEn && (
              <p>
                <b>{isHi ? 'जैमिनी चर भूमिका: ' : 'Jaimini Role: '}</b>
                {isHi ? karaka.jaiminiSignificanceHi : karaka.jaiminiSignificanceEn}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Retrograde Status Box if active */}
      {isRetrograde && (
        <div className="p-2 rounded-lg bg-amber-50/70 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 text-[10.5px] space-y-0.5 text-amber-950 dark:text-amber-100">
          <div className="font-bold text-amber-900 dark:text-amber-300 flex items-center gap-1">
            <Activity className="w-3.5 h-3.5 text-amber-700 shrink-0" />
            <span>{isHi ? 'वक्री स्थिति (Retrograde / Vakra Motion):' : 'Retrograde Motion (Vakra Gati):'}</span>
          </div>
          <p className="leading-snug">
            {isHi
              ? 'वक्री होने से ग्रह का चेष्टा बल (Motional strength) अत्यधिक बढ़ जाता है। ग्रह अपनी सामान्य बाह्य दिशा के स्थान पर अंतर्मुखी व गहन कर्मिक सामर्थ्य से कार्य करता है।'
              : 'Heightens Cheshta Bala (motional power). The planet acts with deep internal introspection, non-linear creativity, and intense tenacity to fulfill karmic directives.'}
          </p>
        </div>
      )}

      {/* Exaltation & Debilitation Reference */}
      <div className="grid grid-cols-2 gap-1.5 text-[11px]">
        <div className="p-2 rounded-lg bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
          <span className="text-[10px] text-emerald-800 dark:text-emerald-300 font-bold block">
            ↑ {isHi ? 'उच्च राशि व अंश' : 'Exaltation Point'}
          </span>
          <span className="font-semibold text-emerald-900 dark:text-emerald-100">
            {getZodiacName(relationships.exaltationInfo.sign, lang)} @ {relationships.exaltationInfo.degree}°
          </span>
        </div>

        <div className="p-2 rounded-lg bg-rose-50/70 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800">
          <span className="text-[10px] text-rose-800 dark:text-rose-300 font-bold block">
            ↓ {isHi ? 'नीच राशि व अंश' : 'Debilitation Point'}
          </span>
          <span className="font-semibold text-rose-900 dark:text-rose-100">
            {getZodiacName(relationships.debilitationInfo.sign, lang)} @ {relationships.debilitationInfo.degree}°
          </span>
        </div>
      </div>

      {/* Dispositor (House Lord) & Panchadha Compound Relationship */}
      <div className="p-2.5 rounded-lg bg-astro-bg/80 border border-astro-border/80 text-[11px] space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-astro-text">
            {isHi ? 'स्थित राशि स्वामी (House Lord / Dispositor):' : 'Occupied Sign Lord (Dispositor):'}
          </span>
          <span className="font-bold text-astro-primary">{getPlanetFullName(relationships.dispositor, lang)}</span>
        </div>
        <div className="flex items-center justify-between border-t border-astro-border/50 pt-1">
          <span className="text-astro-secondary-text">{isHi ? 'पंचधा मैत्री संबंध:' : 'Panchadha Compound Rel:'}</span>
          <span className="font-bold text-astro-text">
            {isHi ? relationships.compoundWithDispositorHi : relationships.compoundWithDispositor}
          </span>
        </div>
        <div className="text-[10px] text-astro-secondary-text">
          {isHi
            ? `(नैसर्गिक: ${relationships.naturalFriends.includes(relationships.dispositor) ? 'मित्र' : relationships.naturalEnemies.includes(relationships.dispositor) ? 'शत्रु' : 'सम'} | तात्कालिक: ${relationships.temporalFriends.includes(relationships.dispositor) ? 'मित्र' : 'शत्रु'})`
            : `(Natural: ${relationships.naturalFriends.includes(relationships.dispositor) ? 'Friend' : relationships.naturalEnemies.includes(relationships.dispositor) ? 'Enemy' : 'Neutral'} | Temporal: ${relationships.temporalFriends.includes(relationships.dispositor) ? 'Friend' : 'Enemy'})`}
        </div>
      </div>

      {/* Lagna Lord Alignment */}
      <div className="p-2 rounded-lg bg-astro-card border border-astro-border text-[10.5px] space-y-0.5">
        <div className="flex items-center justify-between">
          <span className="text-astro-secondary-text">{isHi ? 'कुंडली लग्नेश (Lagna Lord):' : 'Chart Lagna Lord:'}</span>
          <span className="font-bold text-astro-primary">{getPlanetFullName(relationships.lagnaLord, lang)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-astro-secondary-text">{isHi ? 'लग्नेश से संबंध:' : 'Rel to Lagna Lord:'}</span>
          <span className="font-semibold text-astro-text">
            {isHi ? relationships.lagnaLordRelationship.compoundHi : relationships.lagnaLordRelationship.compound}
          </span>
        </div>
      </div>

      {/* Friends & Enemies List */}
      <div className="text-[10px] space-y-1 pt-1 border-t border-astro-border/60">
        <div className="flex gap-1 items-baseline">
          <span className="font-bold text-emerald-700 shrink-0">{isHi ? 'नैसर्गिक मित्र:' : 'Natural Friends:'}</span>
          <span className="text-astro-secondary-text truncate">
            {relationships.naturalFriends.length > 0
              ? relationships.naturalFriends.map(f => getPlanetFullName(f, lang)).join(', ')
              : isHi ? 'कोई नहीं' : 'None'}
          </span>
        </div>
        <div className="flex gap-1 items-baseline">
          <span className="font-bold text-rose-700 shrink-0">{isHi ? 'नैसर्गिक शत्रु:' : 'Natural Enemies:'}</span>
          <span className="text-astro-secondary-text truncate">
            {relationships.naturalEnemies.length > 0
              ? relationships.naturalEnemies.map(e => getPlanetFullName(e, lang)).join(', ')
              : isHi ? 'कोई नहीं' : 'None'}
          </span>
        </div>
      </div>

      {/* Neecha Bhanga Box if active */}
      {neechaBhanga.isActive && (
        <div className="p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-300 dark:border-amber-700 text-[10px] space-y-1 text-amber-900 dark:text-amber-200">
          <div className="flex items-center gap-1 font-bold">
            <Sparkles className="w-3.5 h-3.5 text-amber-600 shrink-0" />
            <span>{isHi ? 'नीच भंग राजयोग प्राप्त' : 'Neecha Bhanga Raja Yoga Applied'}</span>
          </div>
          <p className="text-[10px] font-semibold text-amber-900 dark:text-amber-200">
            {isHi ? neechaBhanga.descriptionHi : neechaBhanga.descriptionEn}
          </p>
          <ul className="list-disc pl-4 space-y-0.5 text-[10px] text-amber-800 dark:text-amber-300">
            {neechaBhanga.reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------
 * 2b. Bhavesha (House Lord) Relationship & Portfolio Hover Content
 * ------------------------------------------------------------- */
export const BhaveshaRelationshipHoverCard: React.FC<{
  planetName: PlanetName;
  houseLordName: PlanetName;
  houseNumber: number;
  bhaveshaRel: {
    natural: string;
    temporal: string;
    compound: string;
    naturalHi: string;
    temporalHi: string;
    compoundHi: string;
    compoundEn: string;
    isLord: boolean;
  };
  ruledHouses: number[];
  enhancedDignity?: EnhancedPlanetaryDignity;
  lang: Language;
}> = ({
  planetName,
  houseLordName,
  houseNumber,
  bhaveshaRel,
  ruledHouses,
  enhancedDignity,
  lang
}) => {
  const isHi = lang === 'hi';
  const isLord = bhaveshaRel.isLord;

  return (
    <div className="space-y-2.5 text-xs text-astro-text">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-astro-border/70 pb-2">
        <div className="flex items-center gap-1.5 font-bold text-astro-primary text-sm font-serif">
          <Layers className="w-4 h-4 text-astro-primary shrink-0" />
          <span>{getPlanetFullName(planetName, lang)} {isHi ? 'भावेश संबंध' : 'Bhavesha Relationship'}</span>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
          isLord
            ? 'bg-amber-100 text-amber-900 border-amber-300'
            : bhaveshaRel.compound === 'Great Friend' || bhaveshaRel.compound === 'Friend'
            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
            : bhaveshaRel.compound === 'Great Enemy' || bhaveshaRel.compound === 'Enemy'
            ? 'bg-rose-50 text-rose-800 border-rose-200'
            : 'bg-astro-card text-astro-secondary-text border-astro-border'
        }`}>
          {isHi ? bhaveshaRel.compoundHi : isLord ? 'Own House (Lord)' : bhaveshaRel.compoundEn}
        </span>
      </div>

      {/* Explanation Banner */}
      <div className="p-2.5 rounded-lg bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 space-y-1 text-[11px]">
        <div className="flex items-center gap-1 font-bold text-amber-900 dark:text-amber-300 text-[10.5px]">
          <BookOpen className="w-3.5 h-3.5 text-amber-700 shrink-0" />
          <span>{isHi ? `भाव ${houseNumber} स्वामी (${getPlanetFullName(houseLordName, lang)}) से संबंध:` : `Relation with House ${houseNumber} Lord (${houseLordName}):`}</span>
        </div>
        <p className="text-amber-950 dark:text-amber-100">
          {isLord ? (
            isHi
              ? `यह ग्रह स्वयं भाव ${houseNumber} का अधिपति (भावेश) है। यह इस भाव के पूर्ण स्वामित्व व संरक्षण का दायित्व निभाता है।`
              : `This planet is itself the sovereign Lord (Bhavesha) of House ${houseNumber}, holding full dominion and guardianship over its matters.`
          ) : (
            isHi
              ? `भावेश ${getPlanetFullName(houseLordName, lang)} के साथ ${getPlanetFullName(planetName, lang)} का संबंध '${bhaveshaRel.compoundHi}' है। अनुकूल संबंध होने पर ग्रह भाव के कार्य में सहायता करता है।`
              : `${planetName} holds a '${bhaveshaRel.compoundEn}' dynamic with House Lord ${houseLordName}. A favorable bond allows harmonious execution of house portfolios.`
          )}
        </p>
      </div>

      {/* 5-Fold Maitri Breakdown */}
      {!isLord && (
        <div className="p-2.5 rounded-lg bg-astro-bg/80 border border-astro-border/80 text-[11px] space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-astro-secondary-text">{isHi ? 'नैसर्गिक संबंध (Natural):' : 'Natural Friendship:'}</span>
            <span className="font-bold text-astro-text">{isHi ? bhaveshaRel.naturalHi : bhaveshaRel.natural}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-astro-secondary-text">{isHi ? 'तात्कालिक संबंध (Temporal):' : 'Temporal Friendship:'}</span>
            <span className="font-bold text-astro-text">{isHi ? bhaveshaRel.temporalHi : bhaveshaRel.temporal}</span>
          </div>
          <div className="flex items-center justify-between border-t border-astro-border/50 pt-1">
            <span className="font-semibold text-astro-primary">{isHi ? 'संयुक्त पंचधा मैत्री:' : 'Compound Panchadha:'}</span>
            <span className="font-bold text-astro-primary">{isHi ? bhaveshaRel.compoundHi : bhaveshaRel.compoundEn}</span>
          </div>
        </div>
      )}

      {/* Ruled Houses / Portfolios */}
      <div className="p-2 rounded-lg bg-astro-card border border-astro-border text-[11px] space-y-1">
        <span className="font-semibold text-astro-text block">
          {isHi ? 'लग्न से भाव स्वामित्व (Lordship Portfolio):' : 'Houses Ruled from Lagna:'}
        </span>
        <p className="text-astro-secondary-text text-[10.5px] leading-relaxed">
          {ruledHouses.length > 0 ? (
            isHi
              ? `${getPlanetFullName(planetName, lang)} लग्न कुंडली में भाव ${ruledHouses.join(' और ')} का स्वामी है। यह उन भावों की ऊर्जा को भाव ${houseNumber} में संयोजित करता है।`
              : `${planetName} rules House ${ruledHouses.join(' & ')} from Ascendant, channeling those portfolios into House ${houseNumber}.`
          ) : (
            isHi
              ? 'यह ग्रह छाया ग्रह है, जिसका अपना कोई राशि स्वामित्व नहीं होता; यह अपनी युति व दृष्टि अनुसार फल देता है।'
              : 'This is a lunar node/shadow entity without individual sign lordships; operates as a karmic proxy.'
          )}
        </p>
      </div>

      {/* Lagna Lord Alignment Info */}
      {enhancedDignity && (
        <div className="flex items-center justify-between text-[10px] px-2 py-1 rounded bg-astro-bg border border-astro-border text-astro-secondary-text">
          <span>{isHi ? 'लग्नेश से संबंध:' : 'Rel to Lagna Lord:'}</span>
          <span className="font-semibold text-astro-text">
            {isHi ? enhancedDignity.relationships.lagnaLordRelationship.compoundHi : enhancedDignity.relationships.lagnaLordRelationship.compound}
          </span>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------
 * 3. Shadbala Hover Content (with Why & Significance)
 * ------------------------------------------------------------- */
export const ShadbalaHoverCard: React.FC<{
  item: EnhancedPlanetaryDignity;
  lang: Language;
}> = ({ item, lang }) => {
  const isHi = lang === 'hi';
  const sb = item.shadbala;

  if (!sb) {
    return (
      <div className="p-2 text-xs text-astro-secondary-text">
        {isHi ? 'इस ग्रह हेतु षड्बल गणना उपलब्ध नहीं है (छाया ग्रह)' : 'Shadbala not calculated for Node/Shadow Planet'}
      </div>
    );
  }

  const percent = Math.round(sb.ratio * 100);

  const subBalas = [
    { labelEn: 'Sthana Bala (Positional)', labelHi: 'स्थान बल (स्थानिक)', val: sb.sthanaBala, max: 200, whyEn: 'Strength from sign, exaltation, vargas & quadrant placement.', whyHi: 'उच्च राशि, वर्ग कुंडलियों व केंद्र स्थान से प्राप्त बल।' },
    { labelEn: 'Dig Bala (Directional)', labelHi: 'दिग् बल (दिशागत)', val: sb.digBala, max: 60, whyEn: 'Strength from directional affinity (e.g., Jupiter in 1st, Sun in 10th).', whyHi: 'विशिष्ट दिशाओं (जैसे पूर्व में गुरु, दक्षिण में सूर्य) में शक्ति।' },
    { labelEn: 'Kaala Bala (Temporal)', labelHi: 'काल बल (समयगत)', val: sb.kaalaBala, max: 100, whyEn: 'Strength from day/night birth, paksha & seasonal cycles.', whyHi: 'दिन/रात्रि जन्म, शुक्ल/कृष्ण पक्ष व ऋतुओं से प्राप्त बल।' },
    { labelEn: 'Cheshta Bala (Motional)', labelHi: 'चेष्टा बल (गतिगत)', val: sb.cheshtaBala, max: 60, whyEn: 'Strength from retrograde motion and planetary velocity.', whyHi: 'वक्री गति अथवा तेज चाल से अर्जित सक्रिय ऊर्जा।' },
    { labelEn: 'Naisargika Bala (Natural)', labelHi: 'नैसर्गिक बल (तेजस्वी)', val: sb.naisargikaBala, max: 60, whyEn: 'Fixed luminous strength by cosmic hierarchy (Sun > Moon > Venus > ...).', whyHi: 'ब्रह्मांडीय तेज का स्थायी क्रम (सूर्य > चंद्र > शुक्र...)।' },
    { labelEn: 'Drik Bala (Aspectual)', labelHi: 'दृग् बल (दृष्टिगत)', val: sb.drikBala, max: 40, whyEn: 'Strength received from benefic vs malefic aspects.', whyHi: 'शुभ ग्रहों की दृष्टि से वृद्धि एवं पाप दृष्टि से कमी।' }
  ];

  return (
    <div className="space-y-2.5 text-xs text-astro-text">
      <div className="flex items-center justify-between border-b border-astro-border/70 pb-2">
        <div className="flex items-center gap-1.5 font-bold text-astro-primary text-sm font-serif">
          <Zap className="w-4 h-4 text-astro-primary shrink-0" />
          <span>{getPlanetFullName(item.planet, lang)} {isHi ? 'षड्बल सामर्थ्य' : 'Shadbala Strength'}</span>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
          percent >= 120 ? 'bg-emerald-100 text-emerald-900 border-emerald-300' :
          percent >= 100 ? 'bg-teal-100 text-teal-900 border-teal-300' :
          percent >= 80 ? 'bg-amber-100 text-amber-900 border-amber-300' :
          'bg-rose-100 text-rose-900 border-rose-300'
        }`}>
          {percent}% ({sb.strengthCategory})
        </span>
      </div>

      {/* WHY & ASTROLOGICAL SIGNIFICANCE */}
      <div className="p-2.5 rounded-lg bg-indigo-50/60 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800 space-y-1 text-[11px]">
        <div className="flex items-center gap-1 font-bold text-indigo-900 dark:text-indigo-300 text-[10.5px]">
          <Activity className="w-3.5 h-3.5 text-indigo-700 shrink-0" />
          <span>{isHi ? 'षड्बल का कारण एवं ज्योतिषीय महत्व:' : 'Why Shadbala & Astrological Significance:'}</span>
        </div>
        <p className="text-indigo-950 dark:text-indigo-100">
          <b>{isHi ? 'कारण (Why): ' : 'Why: '}</b>
          {isHi
            ? `${getPlanetFullName(item.planet, lang)} ने 6 स्रोतों से कुल ${sb.totalRupas.toFixed(2)} रूप अर्जित किए हैं (${sb.ratio >= 1.0 ? 'अपेक्षित 100% से अधिक बलवान' : 'अपेक्षित से कम'})।`
            : `${item.planet} accumulated ${sb.totalRupas.toFixed(2)} Rupas across 6 planetary vectors (${sb.ratio >= 1.0 ? 'fully empowered' : 'below threshold'}).`}
        </p>
        <p className="text-indigo-950 dark:text-indigo-100">
          <b>{isHi ? 'महत्व (Significance): ' : 'Significance: '}</b>
          {isHi
            ? (sb.ratio >= 1.0
                ? 'यह ग्रह अपनी महादशा व गोचर में अपने वादों व फल को भौतिक संसार में पूर्ण रूप से साकार करने की वास्तविक क्षमता रखता है।'
                : 'ग्रह में भौतिक धरातल पर फल देने में अतिरिक्त प्रयास व उपायों की आवश्यकता रहती है।')
            : (sb.ratio >= 1.0
                ? 'The planet possesses robust physical capacity to materialize its promises during Dasha & transits.'
                : 'The planet may struggle to manifest concrete events effortlessly, needing conscious remedies.')}
        </p>
      </div>

      {/* Rupas Summary */}
      <div className="grid grid-cols-2 gap-2 text-[11px] bg-astro-bg/80 p-2 rounded-lg border border-astro-border/80">
        <div>
          <span className="text-[10px] text-astro-secondary-text block uppercase font-semibold">
            {isHi ? 'प्राप्त रूप (Total Rupas)' : 'Total Rupas'}
          </span>
          <span className="font-bold text-astro-primary font-mono text-xs">
            {sb.totalRupas.toFixed(2)} R ({Math.round(sb.totalVirupas)} Virupas)
          </span>
        </div>
        <div>
          <span className="text-[10px] text-astro-secondary-text block uppercase font-semibold">
            {isHi ? 'अपेक्षित रूप (Required)' : 'Required Rupas'}
          </span>
          <span className="font-bold text-astro-text font-mono text-xs">
            {sb.requiredRupas.toFixed(2)} R
          </span>
        </div>
      </div>

      {/* 6 Sub-Balas Micro-Meters */}
      <div className="space-y-1.5 pt-1">
        <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block">
          {isHi ? 'षड्बल 6 घटकों का विवरण (Virupas)' : '6-Fold Bala Breakdown (Virupas)'}
        </span>
        {subBalas.map((bala, idx) => {
          const ratio = Math.min(100, Math.max(0, (bala.val / (bala.max || 60)) * 100));
          return (
            <div key={idx} className="space-y-0.5">
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-astro-text font-medium">{isHi ? bala.labelHi : bala.labelEn}</span>
                <span className="font-mono text-astro-secondary-text font-semibold">{Math.round(bala.val)}</span>
              </div>
              <div className="h-1.5 w-full bg-astro-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-astro-primary rounded-full transition-all duration-300"
                  style={{ width: `${ratio}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------
 * 4. Nature & Lagna Lord Relationship Hover Content
 * ------------------------------------------------------------- */
export const NatureHoverCard: React.FC<{
  item: EnhancedPlanetaryDignity;
  lang: Language;
}> = ({ item, lang }) => {
  const isHi = lang === 'hi';
  const { relationships, isYogakaraka, functionalNature } = item;

  return (
    <div className="space-y-2.5 text-xs text-astro-text">
      <div className="flex items-center justify-between border-b border-astro-border/70 pb-2">
        <div className="flex items-center gap-1.5 font-bold text-astro-primary text-sm font-serif">
          <Shield className="w-4 h-4 text-astro-primary shrink-0" />
          <span>{getPlanetFullName(item.planet, lang)} {isHi ? 'कार्यात्मक भूमिका' : 'Functional Role & Nature'}</span>
        </div>
        {isYogakaraka ? (
          <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 border border-amber-400 text-[10px] font-bold inline-flex items-center gap-1">
            <Star className="w-3 h-3 fill-amber-500 text-amber-600" />
            {isHi ? 'योगकारक' : 'Yogakaraka'}
          </span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-astro-bg border border-astro-border text-astro-primary">
            {isHi ? item.functionalNatureHi : item.functionalNature}
          </span>
        )}
      </div>

      {/* WHY & ASTROLOGICAL SIGNIFICANCE */}
      <div className="p-2.5 rounded-lg bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 space-y-1 text-[11px]">
        <div className="flex items-center gap-1 font-bold text-emerald-900 dark:text-emerald-300 text-[10.5px]">
          <Shield className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
          <span>{isHi ? 'कार्यात्मक प्रकृति का कारण एवं महत्व:' : 'Functional Nature & Significance:'}</span>
        </div>
        <p className="text-emerald-950 dark:text-emerald-100">
          <b>{isHi ? 'कारण (Why): ' : 'Why: '}</b>
          {isHi
            ? `पाराशरी नियमानुसार लग्नानुसार त्रिकोण (1, 5, 9) व केंद्र स्वामियों की शुभता तथा त्रिक भाव (6, 8, 12) के आधिपत्य के आधार पर यह भूमिका निर्धारित होती है।`
            : `Determined by classical Parashari lordship rules combining Kendra (1, 4, 7, 10), Trikona (1, 5, 9), and Dusthana (6, 8, 12) portfolios for this Ascendant.`}
        </p>
        <p className="text-emerald-950 dark:text-emerald-100">
          <b>{isHi ? 'महत्व (Significance): ' : 'Significance: '}</b>
          {isHi
            ? (isYogakaraka
                ? 'यह ग्रह जीवन में उच्चतम राजयोग, सफलता व धर्म-कर्म में अभूतपूर्व प्रगति कराता है।'
                : functionalNature.includes('Benefic')
                ? 'यह जातक के जीवन में सहज सहयोग, स्वास्थ्य व सकारात्मक अवसरों का सृजन करता है।'
                : 'यह जातक को कर्मिक चुनौतियां, आत्म-नियंत्रण व परिपक्वता के पाठ सिखाता है।')
            : (isYogakaraka
                ? 'Single-handedly capable of bestowing elite fortunes, professional status, and dharmic prosperity.'
                : functionalNature.includes('Benefic')
                ? 'Creates natural avenues of expansion, harmonious relationships, and auspicious breakthroughs.'
                : 'Acts as a strict karmic teacher, cultivating resilience, grit, and spiritual transformation.')}
        </p>
      </div>

      {/* Relationship with Lagna Lord */}
      <div className="p-2.5 rounded-lg bg-astro-bg/80 border border-astro-border/80 text-[11px] space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-astro-text">
            {isHi ? 'लग्नेश (Lagna Lord):' : 'Lagna Lord:'}
          </span>
          <span className="font-bold text-astro-primary">
            {getPlanetFullName(relationships.lagnaLord, lang)}
          </span>
        </div>

        <div className="flex items-center justify-between border-t border-astro-border/50 pt-1">
          <span className="text-astro-secondary-text">{isHi ? 'लग्नेश से संबंध:' : 'Rel with Lagna Lord:'}</span>
          <span className="font-bold text-astro-text">
            {isHi ? relationships.lagnaLordRelationship.compoundHi : relationships.lagnaLordRelationship.compound}
          </span>
        </div>

        <div className="text-[10px] text-astro-secondary-text">
          {isHi
            ? `नैसर्गिक: ${relationships.lagnaLordRelationship.natural === 'Friend' ? 'मित्र' : relationships.lagnaLordRelationship.natural === 'Enemy' ? 'शत्रु' : 'सम'} | तात्कालिक: ${relationships.lagnaLordRelationship.temporal === 'Friend' ? 'मित्र' : 'शत्रु'}`
            : `Natural: ${relationships.lagnaLordRelationship.natural} | Temporal: ${relationships.lagnaLordRelationship.temporal}`}
        </div>
      </div>

      {/* House Lordships Summary */}
      <div className="p-2 rounded-lg bg-astro-card border border-astro-border text-[11px] space-y-1">
        <span className="font-semibold text-astro-text block">
          {isHi ? 'भाव स्वामित्व व फल:' : 'House Lordships & Impact:'}
        </span>
        <p className="text-astro-secondary-text leading-relaxed">
          {item.natureRoleDescription}
        </p>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------
 * 5. Nakshatra & Cosmic Impact Hover Content
 * ------------------------------------------------------------- */
export const NakshatraHoverCard: React.FC<{
  item: EnhancedPlanetaryDignity;
  lang: Language;
}> = ({ item, lang }) => {
  const isHi = lang === 'hi';
  const { nakshatraImpact } = item;

  return (
    <div className="space-y-2.5 text-xs text-astro-text">
      <div className="flex items-center justify-between border-b border-astro-border/70 pb-2">
        <div className="flex items-center gap-1.5 font-bold text-astro-primary text-sm font-serif">
          <Sparkles className="w-4 h-4 text-astro-primary shrink-0" />
          <span>{getNakshatraName(nakshatraImpact.nakshatra, lang)} ({isHi ? `पद ${nakshatraImpact.pada}` : `Pada ${nakshatraImpact.pada}`})</span>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border inline-flex items-center gap-1 ${
          nakshatraImpact.isTarabalaFavorable
            ? 'bg-emerald-100 text-emerald-900 border-emerald-300'
            : 'bg-amber-100 text-amber-900 border-amber-300'
        }`}>
          {nakshatraImpact.isTarabalaFavorable ? (
            <CheckCircle className="w-2.5 h-2.5 text-emerald-600" />
          ) : (
            <AlertCircle className="w-2.5 h-2.5 text-amber-600" />
          )}
          {isHi ? nakshatraImpact.navtaraTypeHi.split(' ')[0] : nakshatraImpact.navtaraType.split(' ')[0]}
        </span>
      </div>

      {/* WHY & ASTROLOGICAL SIGNIFICANCE */}
      <div className="p-2.5 rounded-lg bg-teal-50/60 dark:bg-teal-950/20 border border-teal-200 dark:border-teal-800 space-y-1 text-[11px]">
        <div className="flex items-center gap-1 font-bold text-teal-900 dark:text-teal-300 text-[10.5px]">
          <Compass className="w-3.5 h-3.5 text-teal-700 shrink-0" />
          <span>{isHi ? 'नक्षत्र व ताराबल का कारण एवं महत्व:' : 'Nakshatra & Tarabala Significance:'}</span>
        </div>
        <p className="text-teal-950 dark:text-teal-100">
          <b>{isHi ? 'कारण (Why): ' : 'Why: '}</b>
          {isHi
            ? `जन्म चंद्र नक्षत्र से गणना करने पर यह तारा '${nakshatraImpact.navtaraTypeHi}' क्षेत्र में स्थित है, जिसके स्वामी ${getPlanetFullName(nakshatraImpact.lord, lang)} हैं।`
            : `Positioned in the '${nakshatraImpact.navtaraType}' zone counted from natal Moon, ruled by ${nakshatraImpact.lord}.`}
        </p>
        <p className="text-teal-950 dark:text-teal-100">
          <b>{isHi ? 'महत्व (Significance): ' : 'Significance: '}</b>
          {isHi
            ? (nakshatraImpact.isTarabalaFavorable
                ? 'यह तारा मानसिक शांति, कार्यों में सहज सिद्धि तथा महादशा/अंतर्दशा में शुभ फलों की प्राप्ति कराता है।'
                : 'यह तारा कर्मिक सतर्कता, धैर्य एवं योजनाओं में अतिरिक्त सावधानी बरतने का संकेत देता है।')
            : (nakshatraImpact.isTarabalaFavorable
                ? 'Yields psychological ease, smooth fortune, and favorable timing for worldly initiatives.'
                : 'Calls for disciplined mindfulness and protective remedies during related periods.')}
        </p>
      </div>

      {/* Deity & Cosmic Shakti */}
      <div className="space-y-1.5 bg-astro-bg/80 p-2.5 rounded-lg border border-astro-border/80 text-[11px]">
        <div>
          <span className="text-[10px] text-astro-secondary-text uppercase font-semibold block">
            {isHi ? 'अधिष्ठाता देवता (Presiding Deity)' : 'Presiding Deity'}
          </span>
          <span className="font-bold text-astro-primary">
            {isHi ? nakshatraImpact.deityHi : nakshatraImpact.deity}
          </span>
        </div>

        <div>
          <span className="text-[10px] text-astro-secondary-text uppercase font-semibold block">
            {isHi ? 'नक्षत्र शक्ति (Cosmic Shakti)' : 'Cosmic Shakti'}
          </span>
          <span className="font-medium text-astro-text">
            {isHi ? nakshatraImpact.shaktiHi : nakshatraImpact.shakti}
          </span>
        </div>
      </div>

      {/* Navtara Chakra Analysis */}
      <div className="p-2 rounded-lg bg-astro-card border border-astro-border text-[11px] space-y-1">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-astro-text">{isHi ? 'नवतारा चक्र (जन्म चंद्र से):' : 'Navtara from Natal Moon:'}</span>
          <span className="font-bold text-astro-primary">{isHi ? nakshatraImpact.navtaraTypeHi : nakshatraImpact.navtaraType}</span>
        </div>
        <p className="text-astro-secondary-text text-[10px] leading-relaxed">
          {isHi ? nakshatraImpact.impactDescriptionHi : nakshatraImpact.impactDescriptionEn}
        </p>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------
 * 6. BAV (Bhinna Ashtakavarga) Hover Content
 * ------------------------------------------------------------- */
export const BavHoverCard: React.FC<{
  planetName: string;
  signName: string;
  houseNumber: number;
  bavScore?: number;
  contributorsMap?: Record<string, number> | any;
  lang: Language;
}> = ({ planetName, signName, houseNumber, bavScore, contributorsMap, lang }) => {
  const isHi = lang === 'hi';
  const hasBav = bavScore !== undefined && bavScore !== null;
  const score = bavScore ?? 0;

  const getSupportBadge = (val: number) => {
    if (val >= 5) return {
      label: isHi ? 'उच्च बलवान (5-8 बिंदु)' : 'Strong Support (5-8 Bindus)',
      cls: 'bg-emerald-100 text-emerald-900 border-emerald-300',
      noteHi: 'यह ग्रह इस राशि/भाव में अत्यंत फलदायक है। दशा एवं गोचर में उत्तम परिणाम देगा।',
      noteEn: 'High localized strength in this house. Delivers smooth, robust results during active periods.'
    };
    if (val === 4) return {
      label: isHi ? 'मध्यम बल (4 बिंदु)' : 'Moderate (4 Bindus)',
      cls: 'bg-teal-100 text-teal-900 border-teal-300',
      noteHi: 'संतुलित स्थिति। प्रयास के साथ सामान्य व स्थिर फल प्राप्त होंगे।',
      noteEn: 'Balanced localized potency. Yields steady, reliable results with focused action.'
    };
    return {
      label: isHi ? 'न्यून बल (0-3 बिंदु)' : 'Low Support (0-3 Bindus)',
      cls: 'bg-rose-100 text-rose-900 border-rose-300',
      noteHi: 'स्थानीय प्रतिरोध या संघर्ष की संभावना। उपाय एवं धैर्य आवश्यक है।',
      noteEn: 'Localized friction or resistance. Requires conscious remedies and patience.'
    };
  };

  const support = getSupportBadge(score);

  return (
    <div className="space-y-2.5 text-xs text-astro-text">
      <div className="flex items-center justify-between border-b border-astro-border/70 pb-2">
        <div className="flex items-center gap-1.5 font-bold text-astro-primary text-sm font-serif">
          <Layers className="w-4 h-4 text-astro-primary shrink-0" />
          <span>{getPlanetFullName(planetName as any, lang)} — BAV ({isHi ? 'भिन्न अष्टकवर्ग' : 'Bhinna Ashtakavarga'})</span>
        </div>
        {hasBav && (
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${support.cls}`}>
            {score}/8 Bindus
          </span>
        )}
      </div>

      <div className="p-2.5 rounded-lg bg-astro-card border border-astro-border space-y-1.5">
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-astro-secondary-text">{isHi ? 'स्थान:' : 'Placement:'}</span>
          <span className="font-bold text-astro-primary">
            {isHi ? `भाव ${houseNumber} (${signName})` : `House ${houseNumber} (${signName})`}
          </span>
        </div>

        {hasBav ? (
          <>
            <div className="flex items-center justify-between text-[11px] pt-1 border-t border-astro-border/50">
              <span className="text-astro-secondary-text">{isHi ? 'मूल्यांकन:' : 'Rating:'}</span>
              <span className="font-bold text-astro-text">{support.label}</span>
            </div>
            <p className="text-[10px] text-astro-secondary-text leading-relaxed pt-1">
              {isHi ? support.noteHi : support.noteEn}
            </p>
          </>
        ) : (
          <p className="text-[10px] text-amber-700 dark:text-amber-300 italic">
            {isHi ? 'राहु एवं केतु का पारंपरिक अष्टकवर्ग स्कोर लागू नहीं होता।' : 'Standard Ashtakavarga points are not applicable to Rahu & Ketu.'}
          </p>
        )}
      </div>

      {/* Contributor Breakdown if provided */}
      {contributorsMap && Object.keys(contributorsMap).length > 0 && (
        <div className="p-2.5 rounded-lg bg-astro-bg border border-astro-border/80 space-y-1.5">
          <span className="font-bold text-astro-primary text-[10.5px] block">
            {isHi ? 'बिंदुदाता चक्र (8 Contributors):' : 'Bindu Contributors Breakdown:'}
          </span>
          <div className="grid grid-cols-4 gap-1 text-[10px]">
            {Object.entries(contributorsMap).map(([contrib, val]) => (
              <div 
                key={contrib}
                className={`p-1 rounded text-center font-mono font-bold border ${
                  val === 1 
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                    : 'bg-gray-50 text-gray-400 border-gray-200 dark:bg-gray-800/40'
                }`}
              >
                <div>{getPlanetFullName(contrib as any, lang).slice(0, 3)}</div>
                <div className="text-[9px]">{val === 1 ? '+1' : '0'}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------
 * 7. Drishti Aspect Hover Content
 * ------------------------------------------------------------- */
export const DrishtiAspectHoverCard: React.FC<{
  planetName: string;
  aspectType: string;
  fromHouse: number;
  toHouse: number;
  drishtiStrengthVirupas: number;
  isBenefic: boolean;
  lang: Language;
}> = ({ planetName, aspectType, fromHouse, toHouse, drishtiStrengthVirupas, isBenefic, lang }) => {
  const isHi = lang === 'hi';
  const pct = Math.round((drishtiStrengthVirupas / 60) * 100);

  return (
    <div className="space-y-2.5 text-xs text-astro-text">
      <div className="flex items-center justify-between border-b border-astro-border/70 pb-2">
        <div className="flex items-center gap-1.5 font-bold text-astro-primary text-sm font-serif">
          <Activity className="w-4 h-4 text-astro-primary shrink-0" />
          <span>{getPlanetFullName(planetName as any, lang)} — {isHi ? 'दृष्टि प्रभाव' : 'Planetary Aspect (Drishti)'}</span>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold border ${
          isBenefic
            ? 'bg-emerald-100 text-emerald-900 border-emerald-300'
            : 'bg-rose-100 text-rose-900 border-rose-300'
        }`}>
          {isBenefic ? (isHi ? 'शुभ दृष्टि' : 'Benefic Aspect') : (isHi ? 'क्रूर/पाप दृष्टि' : 'Malefic Pressure')}
        </span>
      </div>

      <div className="p-2.5 rounded-lg bg-astro-card border border-astro-border space-y-1.5 text-[11px]">
        <div className="flex items-center justify-between">
          <span className="text-astro-secondary-text">{isHi ? 'दृष्टि का प्रकार:' : 'Aspect Type:'}</span>
          <span className="font-bold text-astro-primary">{aspectType}</span>
        </div>

        <div className="flex items-center justify-between border-t border-astro-border/50 pt-1">
          <span className="text-astro-secondary-text">{isHi ? 'प्रक्षेप भाव:' : 'From House:'}</span>
          <span className="font-bold text-astro-text">
            {isHi ? `भाव ${fromHouse} से भाव ${toHouse} पर` : `House ${fromHouse} ➔ House ${toHouse}`}
          </span>
        </div>

        <div className="flex items-center justify-between border-t border-astro-border/50 pt-1">
          <span className="text-astro-secondary-text">{isHi ? 'दृष्टि बल (Virupas):' : 'Drishti Strength:'}</span>
          <span className="font-mono font-bold text-astro-primary">
            {drishtiStrengthVirupas}/60 ({pct}%)
          </span>
        </div>
      </div>

      <div className="p-2.5 rounded-lg bg-teal-50/60 dark:bg-teal-950/20 border border-teal-200 dark:border-teal-800 text-[10.5px] leading-relaxed text-teal-950 dark:text-teal-100">
        <b>{isHi ? 'ज्योतिषीय प्रभाव: ' : 'Astrological Impact: '}</b>
        {isBenefic
          ? (isHi 
              ? `${getPlanetFullName(planetName as any, lang)} की शुभ दृष्टि इस भाव की सामर्थ्य को बढ़ाती है, तनाव घटाती है और कार्यों में सहायता प्रदान करती है।`
              : `${getPlanetFullName(planetName as any, lang)} casts a protective benefic ray, enhancing house vitality and neutralizing friction.`)
          : (isHi
              ? `${getPlanetFullName(planetName as any, lang)} की दृष्टि इस भाव पर अनुशासनात्मक दबाव व संघर्ष लाती है।`
              : `${getPlanetFullName(planetName as any, lang)} exerts intense planetary pressure, demanding discipline and perseverance.`)}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------
 * 8. D1 ➔ D9 Navamsha Comparison Hover Content
 * ------------------------------------------------------------- */
export const D9NavamshaHoverCard: React.FC<{
  planetName: string;
  d1Sign: string;
  d1Degree: number;
  d9Sign: string;
  d9Lord?: string;
  isVargottama?: boolean;
  isPushkarNavamsha?: boolean;
  isPushkarBhaga?: boolean;
  d9Dignity?: string;
  lang: Language;
}> = ({ planetName, d1Sign, d1Degree, d9Sign, d9Lord, isVargottama, isPushkarNavamsha, isPushkarBhaga, d9Dignity, lang }) => {
  const isHi = lang === 'hi';

  return (
    <div className="space-y-2.5 text-xs text-astro-text">
      <div className="flex items-center justify-between border-b border-astro-border/70 pb-2">
        <div className="flex items-center gap-1.5 font-bold text-astro-primary text-sm font-serif">
          <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
          <span>{getPlanetFullName(planetName as any, lang)} — {isHi ? 'D1 ➔ D9 नवमांश स्थिति' : 'D1 ➔ D9 Navamsha Status'}</span>
        </div>
        {isVargottama && (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-900 border border-amber-300">
            ★ Vargottama
          </span>
        )}
      </div>

      <div className="p-2.5 rounded-lg bg-astro-card border border-astro-border space-y-1.5 text-[11px]">
        <div className="flex items-center justify-between">
          <span className="text-astro-secondary-text">{isHi ? 'D1 राशि व अंश:' : 'D1 Rashi & Degree:'}</span>
          <span className="font-bold text-astro-primary">{getZodiacName(d1Sign, lang)} {d1Degree.toFixed(1)}°</span>
        </div>

        <div className="flex items-center justify-between border-t border-astro-border/50 pt-1">
          <span className="text-astro-secondary-text">{isHi ? 'D9 नवमांश राशि:' : 'D9 Navamsha Sign:'}</span>
          <span className="font-bold text-indigo-700 dark:text-indigo-300">
            {getZodiacName(d9Sign, lang)} {d9Lord ? `(${isHi ? 'स्वामी: ' : 'Lord: '}${getPlanetFullName(d9Lord as any, lang)})` : ''}
          </span>
        </div>

        {d9Dignity && (
          <div className="flex items-center justify-between border-t border-astro-border/50 pt-1">
            <span className="text-astro-secondary-text">{isHi ? 'D9 नवमांश सम्मान:' : 'D9 Navamsha Dignity:'}</span>
            <span className="font-bold text-emerald-800 dark:text-emerald-300">{d9Dignity}</span>
          </div>
        )}
      </div>

      {/* Special Highlights */}
      {(isVargottama || isPushkarNavamsha || isPushkarBhaga) && (
        <div className="p-2 rounded-lg bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 space-y-1 text-[10.5px]">
          <span className="font-bold text-amber-900 dark:text-amber-200 block">
            {isHi ? 'विशेष आत्मिक शक्ति:' : 'Special Karmic Strengths:'}
          </span>
          <ul className="list-disc pl-3.5 space-y-0.5 text-amber-950 dark:text-amber-100">
            {isVargottama && (
              <li><b>Vargottama:</b> {isHi ? 'D1 व D9 में एक ही राशि। फल देने में अत्यंत स्थिर व प्रतिबद्ध।' : 'Same sign in D1 & D9. Grants extraordinary stability and strength.'}</li>
            )}
            {isPushkarNavamsha && (
              <li><b>Pushkar Navamsha:</b> {isHi ? 'अत्यंत पोषणकारी नवमांश। जीवन में भाग्य व पुनरुद्धार प्रदान करता है।' : 'Nourishing navamsha. Grants resilience and spiritual fortune.'}</li>
            )}
            {isPushkarBhaga && (
              <li><b>Pushkar Bhaga:</b> {isHi ? 'परम शुभ अंश स्थान। अटूट सुरक्षा प्रदान करता है।' : 'Auspicious degree point giving supreme protection.'}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};


